import React, { FC, useCallback } from "react";

import { faColumns } from "@fortawesome/pro-solid-svg-icons/faColumns";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { Button } from "@mapmycustomers/ui";

import DownloadListDataConfirmModal from "@app/component/ExportListDataConfirmModal";
import localSettings from "@app/config/LocalSettings";
import Search from "@app/scene/dashboard/components/Search";
import StackRankViewState from "@app/scene/dashboard/store/stackRank/StackRankViewState";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./Actions.module.scss";
import SelectColumnsPane from "./SelectColumnsPane";

export interface HeaderProps {
  onExport?: () => void;
  onQueryChange?: (query: string) => void;
  query?: string;
  viewState: StackRankViewState;
}

const Actions: FC<HeaderProps> = ({ onExport, onQueryChange, query, viewState }) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [selectColumnsPaneVisible, showSelectColumns, hideSelectColumns] = useBoolean();
  const [exportConfirmationVisible, showExportConfirmation, hideExportConfirmation] = useBoolean();

  useChangeTracking(() => {
    if (selectColumnsPaneVisible) {
      analytics.clicked(["Show/Hide Columns"]);
    }
  }, [analytics, selectColumnsPaneVisible]);

  const handleExportListData = useCallback(() => {
    if (localSettings.getDontShowExportListDataConfirmation(EntityType.LEADERBOARD)) {
      analytics.clicked(["Export Data"]);
      onExport?.();
    } else {
      showExportConfirmation();
    }
  }, [analytics, onExport, showExportConfirmation]);

  const handleExportListDataConfirm = useCallback(
    (dontShowAgain: boolean) => {
      if (dontShowAgain) {
        localSettings.setDontShowExportListDataConfirmation(EntityType.LEADERBOARD);
      }
      analytics.clicked(["Export Data"]);
      onExport?.();
    },
    [analytics, onExport]
  );

  return (
    <>
      <div className={styles.container}>
        <Search onChange={onQueryChange} value={query} />
        <Tooltip
          placement="bottom"
          title={intl.formatMessage({
            id: "dashboard.stackRankDrillDown.action.showHideColumns",
            defaultMessage: "Hide/Show columns",
            description: "Hide show option for stack rank",
          })}
        >
          <Button
            data-action-id="stack-rank-ddm-columns"
            icon={<FontAwesomeIcon icon={faColumns} />}
            iconOnly
            onClick={showSelectColumns}
            type="text"
          />
        </Tooltip>
        <Tooltip
          placement="bottom"
          title={intl.formatMessage({
            id: "dashboard.stackRankDrillDown.action.export",
            defaultMessage: "Export table data",
            description: "Exports table data for stack rank",
          })}
        >
          <Button
            data-action-id="stack-rank-ddm-export"
            icon={<FontAwesomeIcon icon={faDownload} />}
            iconOnly
            onClick={handleExportListData}
            type="text"
          />
        </Tooltip>
      </div>
      {selectColumnsPaneVisible && (
        <SelectColumnsPane onHide={hideSelectColumns} rankingMetric={viewState.rankingMetric} />
      )}
      {exportConfirmationVisible && (
        <DownloadListDataConfirmModal
          entityType={EntityType.LEADERBOARD}
          onConfirm={handleExportListDataConfirm}
          onHide={hideExportConfirmation}
        />
      )}
    </>
  );
};

export default Actions;
